import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { usePosAuth } from "@/hooks/pos/use-pos-auth";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const MFA_CONFIG_KEY = ["/v1/pos/admin/mfa-config"];

type MfaConfig = {
  totpEnabled: boolean;
  adjacentIntervals?: number;
};

// GET returns { success: true, data: { totpEnabled, adjacentIntervals } }.
// Unwrap data when present, otherwise fall back to a flat shape.
const fetchMfaConfig = async (): Promise<MfaConfig> => {
  const res = await posApiFetch("/v1/pos/admin/mfa-config");
  if (!res.ok) throw new Error("Failed to fetch MFA configuration");
  const json = await res.json().catch(() => null);
  const data = json?.data ?? json ?? {};
  return {
    totpEnabled: !!data.totpEnabled,
    adjacentIntervals: data.adjacentIntervals,
  };
};

const ENABLE_COPY =
  "Users will be able to set up an authenticator app as a second factor when signing in. Existing users without MFA will not be affected until they opt in.";
const DISABLE_COPY =
  "Users with TOTP MFA enrolled will no longer be required to provide a code at sign-in. Existing enrollments remain stored but become inactive.";

export default function SecuritySettingsPage() {
  const { toast } = useToast();
  const { hasRole } = usePosAuth();
  const queryClient = useQueryClient();

  const isAdmin = hasRole("admin");

  useEffect(() => {
    document.title = "Security Settings | SECH Admin";
  }, []);

  // The value the admin is attempting to apply; non-null while the
  // confirmation modal is open.
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: MFA_CONFIG_KEY,
    queryFn: fetchMfaConfig,
    retry: 1,
    enabled: isAdmin,
  });

  const totpEnabled = !!data?.totpEnabled;

  const requestToggle = (next: boolean) => {
    if (saving) return;
    setPendingValue(next);
  };

  const cancelToggle = () => {
    if (saving) return;
    setPendingValue(null);
  };

  const confirmToggle = async () => {
    if (pendingValue === null) return;
    const next = pendingValue;
    setSaving(true);
    try {
      const res = await posApiFetch("/v1/pos/admin/mfa-config", {
        method: "PUT",
        body: JSON.stringify({ totpEnabled: next }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.error?.message || json?.error || json?.message || "Failed to update MFA configuration");
      }
      const confirmed =
        typeof json?.data?.totpEnabled === "boolean"
          ? json.data.totpEnabled
          : typeof json?.totpEnabled === "boolean"
            ? json.totpEnabled
            : next;
      // Write the confirmed state straight into the cache so the toggle
      // reflects the backend without a refetch round-trip.
      queryClient.setQueryData<MfaConfig>(MFA_CONFIG_KEY, (prev) => ({
        ...(prev ?? {}),
        totpEnabled: confirmed,
      }));
      toast({
        title: confirmed ? "TOTP MFA enabled" : "TOTP MFA disabled",
        description: confirmed
          ? "Authenticator-app two-factor authentication is now available to users."
          : "Authenticator-app two-factor authentication has been turned off.",
      });
    } catch (err) {
      // The toggle is driven by query state, so it already shows the old
      // value — surface the failure and leave it untouched (effectively a revert).
      toast({
        title: "Couldn’t update MFA configuration",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
      setPendingValue(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600">
              You do not have permission to view security settings. Admin access is required.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Security Settings</p>
      </div>

      <div className="p-[16px]">
        <div className="max-w-2xl">
          <Card>
            <CardContent className="p-5 sm:p-6">
              <div className="flex flex-col gap-1 mb-5">
                <h2 className="text-[16px] leading-[20px] font-semibold text-[var(--primary-text-color)]">
                  Multi-Factor Authentication
                </h2>
                <p className="text-[13px] leading-5 text-[var(--secondary-text-color)]">
                  Manage authenticator-app (TOTP) two-factor authentication for the Firebase project.
                </p>
              </div>

              {error ? (
                <div className="rounded-[8px] border border-[var(--ui-border-color)] p-4">
                  <p className="text-[13px] text-red-600 mb-3">Failed to load MFA configuration.</p>
                  <Button
                    variant="outline"
                    className="h-[32px] text-[13px]"
                    onClick={() => queryClient.invalidateQueries({ queryKey: MFA_CONFIG_KEY })}
                  >
                    Retry
                  </Button>
                </div>
              ) : isLoading ? (
                <div className="rounded-[8px] border border-[var(--ui-border-color)] p-4 flex items-start gap-4">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-64" />
                  </div>
                  <Skeleton className="h-6 w-11 rounded-full" />
                </div>
              ) : (
                <div className="rounded-[8px] border border-[var(--ui-border-color)] p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {totpEnabled ? (
                        <ShieldCheck className="h-6 w-6 text-[#16A34A] shrink-0 mt-0.5" />
                      ) : (
                        <ShieldOff className="h-6 w-6 text-[var(--secondary-text-color)] shrink-0 mt-0.5" />
                      )}
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[14px] leading-[16px] font-medium text-[var(--primary-text-color)]">
                            Authenticator app (TOTP)
                          </span>
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                              totpEnabled
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-700"
                            )}
                          >
                            {totpEnabled ? "Enabled" : "Disabled"}
                          </span>
                        </div>
                        <p className="text-[13px] leading-5 text-[var(--secondary-text-color)] max-w-md">
                          When enabled, users can add a time-based one-time passcode from an authenticator
                          app (Google Authenticator, Authy, 1Password, etc.) as a second factor at sign-in.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 pt-0.5">
                      {saving && <Loader2 className="h-4 w-4 animate-spin text-[var(--secondary-text-color)]" />}
                      <Switch
                        checked={totpEnabled}
                        disabled={saving}
                        onCheckedChange={requestToggle}
                        aria-label="Toggle TOTP multi-factor authentication"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmModal
        open={pendingValue !== null}
        onClose={cancelToggle}
        onConfirm={confirmToggle}
        title={pendingValue ? "Enable TOTP MFA?" : "Disable TOTP MFA?"}
        description={pendingValue ? ENABLE_COPY : DISABLE_COPY}
        confirmText={saving ? "Saving…" : pendingValue ? "Enable" : "Disable"}
        cancelText="Cancel"
        variant={pendingValue ? "default" : "delete"}
      />
    </div>
  );
}
